import { ArrowDown } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { formatInt } from '../../lib/format.ts';

export interface StepProgressProps {
  /** Steps finished. */
  done: number;
  /** Steps in all, when known. Unknown totals never invent a percent. */
  total?: number | null;
  /** The step in words: "Running the tests". */
  label?: string;
  className?: string;
}

/**
 * Plan as progress (brief 2.3): "Step 3 of 7" as a 2 px determinate bar that fills by transform
 * (scaleX, never width), with the step in words. Without a total it says the count only and
 * shows the indeterminate stripe (M11), whose still twin is a static partial fill.
 */
export function StepProgress({ done, total, label, className }: StepProgressProps) {
  const known = total != null && total > 0;
  const fraction = known ? Math.max(0, Math.min(1, done / total)) : 0;
  const words = known ? `Step ${formatInt(Math.min(done + 1, total))} of ${formatInt(total)}` : `${formatInt(done)} done`;
  return (
    <span className={cn('flex min-w-0 flex-col gap-1', className)}>
      <span className="flex min-w-0 items-baseline justify-between gap-2 text-caption">
        <span className="min-w-0 truncate text-fg-muted">{label}</span>
        <span className="shrink-0 text-fg-subtle num">{words}</span>
      </span>
      {known ? <ProgressBar value={fraction} label={words} /> : <IndeterminateBar label={words} />}
    </span>
  );
}

/** A thin determinate bar on the meter track; the fill moves by scaleX. */
export function ProgressBar({ value, label, tone = 'neutral', className }: { value: number; label: string; tone?: 'neutral' | 'attention'; className?: string }) {
  const v = Math.max(0, Math.min(1, value));
  return (
    <span role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v * 100)} className={cn('relative block h-0.5 w-full overflow-hidden rounded-pill bg-active', className)}>
      <span className={cn('motion-segment absolute inset-0', tone === 'attention' ? 'bg-status-attention-fill' : 'bg-fg-muted')} style={{ scale: `${v} 1` }} />
    </span>
  );
}

/** Work of unknown length (M11): a stripe crosses the bar; under reduced motion a still 40% fill. */
export function IndeterminateBar({ label, className }: { label: string; className?: string }) {
  return (
    <span role="progressbar" aria-label={label} className={cn('relative block h-0.5 w-full overflow-hidden rounded-pill bg-active', className)}>
      <span className="absolute inset-y-0 left-0 w-2/5 rounded-pill bg-fg-muted motion-safe:animate-stripe" />
    </span>
  );
}

export interface ResumeBarProps {
  /** Lines or events that arrived while the person read further up. */
  count: number;
  /** "lines", "events". */
  noun?: string;
  onResume: () => void;
  className?: string;
}

/**
 * The tail that pauses when you read (brief 2.3): shown only while scrolled up, it says what
 * arrived and jumps back to the latest. Scroll never moves because data arrived.
 */
export function ResumeBar({ count, noun = 'lines', onResume, className }: ResumeBarProps) {
  if (count <= 0) return null;
  return (
    <button
      type="button"
      onClick={onResume}
      className={cn('motion-arrive inline-flex h-control-sm items-center gap-1.5 rounded-pill border border-border bg-raised px-3 text-caption font-medium text-fg shadow-elevation-1 hover:border-border-strong', className)}
    >
      <ArrowDown className="size-3.5" aria-hidden />
      {formatInt(count)} new {noun}, jump to latest
    </button>
  );
}

/** A static 2 px end caret on text still streaming (never a blinking cursor). */
export function StreamCaret({ className }: { className?: string }) {
  return <span aria-hidden className={cn('ml-0.5 inline-block h-3.5 w-0.5 translate-y-0.5 rounded-pill bg-fg-muted', className)} />;
}
